/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { UseQueryResult } from '@tanstack/react-query';

import { ApiError, ConfigRevision, GetNodeByIdResponse, Node, NodeConfig } from '@/client';
import { NodeActiveDirectory } from '@/components/Node/NodeActiveDirectory';
import { NodeCredentials } from '@/components/Node/NodeCredentials';
import { NodeHeartbeats } from '@/components/Node/NodeHeartbeats';
import { NodeServerConfig } from '@/components/Node/NodeServerConfig';
import { NodeViewPageCard } from '@/components/Node/NodeViewPageCard';
import { NodeConfigState } from '@/components/NodeConfigState/NodeConfigState';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { NodeDetailsDescription } from '@/pages/NodeDetails/NodeDetailsDescription';
import { Col } from '@/primitives/Col';
import { Flex } from '@/primitives/Flex';
import { Row } from '@/primitives/Row';

export type NodeDetailsOverviewProps = {
  nodeId: string;
  nodeData: GetNodeByIdResponse;
  nodeQuery: UseQueryResult<GetNodeByIdResponse, ApiError>;
  setNodeToShow: (node: Node | undefined) => void;
  setSelectedNodeConfig: (config: NodeConfig | undefined) => void;
  setSelectedConfigRevision: (revision: ConfigRevision | undefined) => void;
  tenantID: string;
};

export function NodeDetailsOverview({
  nodeId,
  nodeData,
  nodeQuery,
  setNodeToShow,
  setSelectedNodeConfig,
  setSelectedConfigRevision,
  tenantID,
}: NodeDetailsOverviewProps) {
  const { t } = useMimicTranslation('node');

  return (
    <div style={{ marginRight: '-24px' }}>
      <Row gutter={[24, 16]}>
        <Col xs={24} xl={12}>
          <Flex vertical>
            <NodeServerConfig node={nodeData.data} loading={nodeQuery.isPending} />
            <NodeViewPageCard title={t('node_description')}>
              <NodeDetailsDescription nodeId={nodeId} nodeQuery={nodeQuery} nodeData={nodeData} />
            </NodeViewPageCard>
          </Flex>
        </Col>
        <Col xs={24} xl={12}>
          <Flex vertical>
            <NodeConfigState
              node={nodeData.data}
              assignNewNodeConfig={() => setNodeToShow(nodeData.data)}
              assignNewConfigRevision={() => {
                setSelectedNodeConfig(nodeData.data.appliedRevisionState?.nodeConfig as unknown as NodeConfig);

                setSelectedConfigRevision({
                  ...nodeData.data.appliedRevisionState?.configRevision,
                  nodeConfigId: nodeData.data.appliedRevisionState?.nodeConfig?.id,
                } as ConfigRevision);

                setNodeToShow(nodeData.data);
              }}
              getNodeConfigPath={(node: Node) =>
                `/tenants/${tenantID}/node-configs/${node.appliedRevisionState?.nodeConfig?.id}`
              }
              getNodeConfigPreviousPath={(node: Node) =>
                `/tenants/${tenantID}/node-configs/${node.previousRevisionState?.nodeConfig?.id}`
              }
              getNodeConfigRevisionPath={(node: Node) =>
                `/tenants/${tenantID}/node-configs/${node.appliedRevisionState?.nodeConfig?.id}/revisions/${node.appliedRevisionState?.configRevision?.revisionNumber}`
              }
              getNodeConfigPreviousRevisionPath={(node: Node) =>
                `/tenants/${tenantID}/node-configs/${node.previousRevisionState?.nodeConfig?.id}/revisions/${node.previousRevisionState?.configRevision?.revisionNumber}`
              }
            />
            <NodeActiveDirectory node={nodeData.data} isPending={nodeQuery.isPending} />
            <NodeCredentials node={nodeData.data} isPending={nodeQuery.isPending} />
            <NodeHeartbeats data={nodeData.data} isPending={nodeQuery.isPending} />
          </Flex>
        </Col>
      </Row>
    </div>
  );
}
